import React, { useContext, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { captureException } from '@sentry/react';
import Snackbar from '@/components/Snackbar';
import { Grid, Typography } from '@material-ui/core';
import { useMutation } from '@apollo/client';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Body1, CardTitle, Title, TabCard, LocationPermissionsText } from '../blocks';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { UPDATE_CLIENT_MILESTONE_THRESHOLDS } from '../mutations';

export const defaultSettings = {
    orderPlacedToScheduled: {
        warning: {
            type: 'duration',
            value: 2,
            units: 'Days',
        },
        error: {
            type: 'duration',
            value: 3,
            units: 'Days',
        },
    },
    pickupScheduledToArrived: {
        warning: {
            type: 'duration',
            value: 2,
            units: 'Days',
        },
        error: {
            type: 'duration',
            value: 3,
            units: 'Days',
        },
    },
    arrivedToReceived: {
        warning: {
            type: 'duration',
            value: 8,
            units: 'Hours',
        },
        error: {
            type: 'duration',
            value: 1,
            units: 'Days',
        },
    },
    receivedToScheduled: {
        warning: {
            type: 'duration',
            value: 1,
            units: 'Days',
        },
        error: {
            type: 'duration',
            value: 2,
            units: 'Days',
        },
    },
    deliveredToComplete: {
        warning: {
            type: 'duration',
            value: 1,
            units: 'Days',
        },
        error: {
            type: 'duration',
            value: 2,
            units: 'Days',
        },
    },
};

const thresholdDescriptions = {
    orderPlacedToScheduled:
        'The order must be scheduled for pickup within X days/hours of the order being placed. The date itself does not matter, as long as the date is scheduled.',
    pickupScheduledToArrived:
        'The order must arrive at the final mile agent dock X days/hours after being picked up from the shipper origin.',
    arrivedToReceived:
        'The order must be marked as received into the final mile agent’s warehouse within X days/hours after the LTL carrier arrived at their dock. OS&D exceptions must be reported.',
    receivedToScheduled:
        'The order must be scheduled for delivery within X days/hours after being received into the final mile agent’s warehouse. The date itself does not matter, as long as the date is scheduled.',
    deliveredToComplete:
        'The order must have POD, photos, and signatures uploaded within X days/hours of the order being marked as delivered.',
};

const milestoneStrings = {
    orderPlacedToScheduled: ['Order Placed', 'Scheduled'],
    pickupScheduledToArrived: ['Pickup Scheduled', 'Arrived (Middle Mile)'],
    arrivedToReceived: ['Arrived', 'Received'],
    receivedToScheduled: ['Received', 'Scheduled'],
    timeframeEnteredToLoaded: ['Timeframe Entered', 'Loaded'],
    loadedToDelivered: ['Loaded', 'Delivered'],
    deliveredToComplete: ['Delivered', 'Complete'],
    orderTenderedToScheduled: ['Order Tendered', 'Scheduled'],
    scheduledToTimeframeEntered: ['Scheduled', 'Timeframe Entered'],
    timeframeEnteredToPickupComplete: ['Timeframe Entered', 'Pickup Complete'],
    pickupCompleteToDelivered: ['Pickup Complete', 'Delivered'],
    pickupCompleteCrossdockToReceived: ['Pickup Complete Crossdock', 'Received'],
};

const milestoneSettingTypeToRender = ({
    milestoneType,
    format,
    value,
    thresholds,
    thresholdsUpdate,
    setThresholdsUpdate,
    thresholdName,
}) => {
    switch (format) {
        case 'duration':
            return (
                <>
                    <Grid item>
                        <TextField
                            select
                            size="small"
                            style={{ maxHeight: '50px' }}
                            variant="outlined"
                            value={thresholds?.[thresholdName]?.[milestoneType]?.value}
                            onChange={(e) => {
                                setThresholdsUpdate({
                                    ...thresholds,
                                    [thresholdName]: {
                                        ...(thresholds?.[thresholdName] || {}),
                                        [milestoneType]: {
                                            ...(thresholds?.[thresholdName]?.[milestoneType] || {}),
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        >
                            {Array.from({ length: 23 }, (_, i) => i + 1).map((i) => (
                                <MenuItem value={i} key={`${thresholdName}-${milestoneType}-${i}`}>
                                    {i}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item>
                        <TextField
                            select
                            size="small"
                            style={{ maxHeight: '50px' }}
                            variant="outlined"
                            value={thresholds?.[thresholdName]?.[milestoneType]?.units}
                            onChange={(e) => {
                                setThresholdsUpdate({
                                    ...thresholds,
                                    [thresholdName]: {
                                        ...(thresholds?.[thresholdName] || {}),
                                        [milestoneType]: {
                                            ...(thresholds?.[thresholdName]?.[milestoneType] || {}),
                                            units: e.target.value,
                                        },
                                    },
                                });
                            }}
                        >
                            <MenuItem value={'Hours'}>Hours</MenuItem>
                            <MenuItem value={'Days'}>Days</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            align-content: center;
                        `}
                    >
                        <Body1>{milestoneType === 'warning' ? 'Warning' : 'Error'}</Body1>
                    </Grid>
                </>
            );
        case 'setTime':
            return (
                <>
                    <Grid item>
                        <TextField
                            select
                            size="small"
                            style={{ maxHeight: '50px' }}
                            variant="outlined"
                            value={thresholds?.[thresholdName]?.[milestoneType]?.value}
                            onChange={(e) => {
                                setThresholdsUpdate({
                                    ...thresholds,
                                    [thresholdName]: {
                                        ...(thresholds?.[thresholdName] || {}),
                                        [milestoneType]: {
                                            ...(thresholds?.[thresholdName]?.[milestoneType] || {}),
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((i) => (
                                <MenuItem value={i} key={`${thresholdName}-${milestoneType}-${i}`}>
                                    {i}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item>
                        <TextField
                            select
                            size="small"
                            style={{ maxHeight: '50px' }}
                            variant="outlined"
                            value={thresholds?.[thresholdName]?.[milestoneType]?.units}
                            onChange={(e) => {
                                setThresholdsUpdate({
                                    ...thresholds,
                                    [thresholdName]: {
                                        ...(thresholds?.[thresholdName] || {}),
                                        [milestoneType]: {
                                            ...(thresholds?.[thresholdName]?.[milestoneType] || {}),
                                            units: e.target.value,
                                        },
                                    },
                                });
                            }}
                        >
                            <MenuItem value={'AM'}>AM</MenuItem>
                            <MenuItem value={'PM'}>PM</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            align-content: center;
                        `}
                    >
                        <Body1>{milestoneType === 'warning' ? 'Warning' : 'Error'}</Body1>
                    </Grid>
                </>
            );
        case 'setDay':
            return (
                <>
                    <Grid item>
                        <TextField
                            select
                            size="small"
                            style={{ maxHeight: '50px' }}
                            variant="outlined"
                            disabled
                        ></TextField>
                    </Grid>
                    <Grid item>
                        <TextField
                            select
                            size="small"
                            style={{ maxHeight: '50px' }}
                            variant="outlined"
                            value={thresholds?.[thresholdName]?.[milestoneType]?.units}
                            onChange={(e) => {
                                setThresholdsUpdate({
                                    ...thresholds,
                                    [thresholdName]: {
                                        ...(thresholds?.[thresholdName] || {}),
                                        [milestoneType]: {
                                            ...(thresholds?.[thresholdName]?.[milestoneType] || {}),
                                            units: e.target.value,
                                        },
                                    },
                                });
                            }}
                        >
                            <MenuItem value={'Day Of'}>Day Of</MenuItem>
                            <MenuItem value={'Next Day'}>Next Day</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            align-content: center;
                        `}
                    >
                        <Body1>{milestoneType === 'warning' ? 'Warning' : 'Error'}</Body1>
                    </Grid>
                </>
            );
    }
};

const MilestoneThresholds = ({ client, callbacks, ...props }) => {
    const [thresholdsUpdate, setThresholdsUpdate] = useState({});

    const thresholds = useMemo(() => {
        const currentThresholds = {
            ...defaultSettings,
            ...client?.milestone_threshold_settings,
            ...thresholdsUpdate,
        };

        return currentThresholds;
    }, [thresholdsUpdate, client]);

    const [saveClient, { loading }] = useMutation(UPDATE_CLIENT_MILESTONE_THRESHOLDS, {
        onError: (error) => {
            captureException(error);
        },
    });

    const handleSave = () => {
        saveClient({
            variables: {
                client_id: client?.client_id,
                update: {
                    milestone_threshold_settings: thresholds,
                },
            },
            onError: (err) => {
                captureException(err);
            },
            onCompleted: () => {
                callbacks?.onSave?.();
            },
        });
    };

    return (
        <>
            <TabCard
                css={css`
                    padding: 2rem;
                `}
                {...props}
            >
                <Grid
                    direction="column"
                    css={css`
                        flex-wrap: nowrap;
                    `}
                >
                    <Grid item>
                        <Title>Milestone Thresholds</Title>
                    </Grid>
                    <Grid
                        item
                        css={css`
                            margin-bottom: 2rem;
                        `}
                    >
                        <Body1>
                            {/* Choose time windows before orders turn orange for warnings and 
                            red for errors on the My
                            Orders table. */}
                            Choose time windows before orders are highlighted on the My Orders table. The highlighting
                            corresponds with the following table:
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    margin-top: 0.5rem;
                                    border: 1px solid #e0e0e0;
                                    width: 400px;
                                `}
                            >
                                {/* Table Header */}
                                <Grid
                                    item
                                    container
                                    css={css`
                                        background-color: #f5f5f5;
                                        padding: 0.5rem 1rem;
                                        border-bottom: 1px solid #e0e0e0;
                                        border-right: 1px solid #e0e0e0;
                                    `}
                                >
                                    <Grid
                                        item
                                        css={css`
                                            width: 75%;
                                        `}
                                    >
                                        <Body1
                                            css={css`
                                                font-weight: bold;
                                            `}
                                        >
                                            Designation
                                        </Body1>
                                    </Grid>
                                    <Grid
                                        item
                                        css={css`
                                            width: 25%;
                                        `}
                                    >
                                        <Body1
                                            css={css`
                                                font-weight: bold;
                                            `}
                                        >
                                            Color
                                        </Body1>
                                    </Grid>
                                </Grid>
                                {/* Warning Row */}
                                <Grid
                                    item
                                    container
                                    css={css`
                                        padding: 0.5rem 1rem;
                                        border-bottom: 1px solid #e0e0e0;
                                        border-right: 1px solid #e0e0e0;
                                    `}
                                >
                                    <Grid
                                        item
                                        css={css`
                                            width: 75%;
                                        `}
                                    >
                                        <Body1>Warning Threshold Exceeded</Body1>
                                    </Grid>
                                    <Grid
                                        item
                                        css={css`
                                            width: 25%;
                                        `}
                                    >
                                        <Body1>Orange</Body1>
                                    </Grid>
                                </Grid>
                                {/* Error Row */}
                                <Grid
                                    item
                                    container
                                    css={css`
                                        padding: 0.5rem 1rem;
                                        border-bottom: 1px solid #e0e0e0;
                                        border-right: 1px solid #e0e0e0;
                                    `}
                                >
                                    <Grid
                                        item
                                        css={css`
                                            width: 75%;
                                        `}
                                    >
                                        <Body1>Error Threshold Exceeded</Body1>
                                    </Grid>
                                    <Grid
                                        item
                                        css={css`
                                            width: 25%;
                                        `}
                                    >
                                        <Body1>Red</Body1>
                                    </Grid>
                                </Grid>
                                <Grid
                                    item
                                    container
                                    css={css`
                                        padding: 0.5rem 1rem;
                                        border-bottom: 1px solid #e0e0e0;
                                        border-right: 1px solid #e0e0e0;
                                    `}
                                >
                                    <Grid
                                        item
                                        css={css`
                                            width: 75%;
                                        `}
                                    >
                                        <Body1>Attempts Made or OSD Exceptions Exist</Body1>
                                    </Grid>
                                    <Grid
                                        item
                                        css={css`
                                            width: 25%;
                                        `}
                                    >
                                        <Body1>Blue</Body1>
                                    </Grid>
                                </Grid>
                                <Grid
                                    item
                                    container
                                    css={css`
                                        padding: 0.5rem 1rem;
                                        border-bottom: 1px solid #e0e0e0;
                                        border-right: 1px solid #e0e0e0;
                                    `}
                                >
                                    <Grid
                                        item
                                        css={css`
                                            width: 75%;
                                        `}
                                    >
                                        <Body1>Order Status is On Hold</Body1>
                                    </Grid>
                                    <Grid
                                        item
                                        css={css`
                                            width: 25%;
                                        `}
                                    >
                                        <Body1>Purple</Body1>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Body1>
                    </Grid>

                    {Object.entries(defaultSettings).map(([thresholdName, thresholdValues]) => {
                        const { warning, error } = thresholdValues;
                        return (
                            <Grid
                                item
                                container
                                css={css`
                                    margin-bottom: 1.75rem;
                                    flex-direction: column;
                                `}
                            >
                                <Grid item>
                                    <LocationPermissionsText>
                                        {milestoneStrings[thresholdName][0]} <ArrowForwardIcon />{' '}
                                        {milestoneStrings[thresholdName][1]}
                                    </LocationPermissionsText>
                                </Grid>
                                <Grid
                                    item
                                    css={css`
                                        margin-bottom: 0.5rem;
                                    `}
                                >
                                    <Body1>{thresholdDescriptions[thresholdName]}</Body1>
                                </Grid>
                                <Grid
                                    item
                                    container
                                    direction="row"
                                    spacing={2}
                                    css={css`
                                        margin-bottom: 0.5rem;
                                    `}
                                >
                                    {milestoneSettingTypeToRender({
                                        milestoneType: 'warning',
                                        format: warning.type,
                                        value: warning.value,
                                        thresholds,
                                        thresholdsUpdate,
                                        setThresholdsUpdate,
                                        thresholdName,
                                    })}
                                </Grid>
                                <Grid item container direction="row" spacing={2}>
                                    {milestoneSettingTypeToRender({
                                        milestoneType: 'error',
                                        format: error.type,
                                        value: error.value,
                                        thresholds,
                                        thresholdsUpdate,
                                        setThresholdsUpdate,
                                        thresholdName,
                                    })}
                                </Grid>
                            </Grid>
                        );
                    })}

                    <Grid
                        item
                        container
                        css={css`
                            margin-bottom: 1.75rem;
                            flex-direction: column;
                        `}
                    >
                        <Grid item>
                            <LocationPermissionsText>Pickup Date/Window</LocationPermissionsText>
                        </Grid>
                        <Grid item css={css``}>
                            <Body1>The order must be picked up on the date scheduled and within the time window.</Body1>
                        </Grid>
                        <Grid
                            item
                            css={css`
                                margin-bottom: 0.5rem;
                            `}
                        >
                            <Body1>
                                (This threshold is not customizable and displays as a warning if the is not en route by
                                the pickup time window start, and as an error if the order is not en route by the pickup
                                time window end.)
                            </Body1>
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        container
                        css={css`
                            margin-bottom: 1.75rem;
                            flex-direction: column;
                        `}
                    >
                        <Grid item>
                            <LocationPermissionsText>Delivery Date/Window</LocationPermissionsText>
                        </Grid>
                        <Grid item css={css``}>
                            <Body1>The order must be delivered on the date scheduled and within the time window.</Body1>
                        </Grid>
                        <Grid
                            item
                            css={css`
                                margin-bottom: 0.5rem;
                            `}
                        >
                            <Body1>
                                (This threshold is not customizable and displays as a warning if the is not en route by
                                the delivery time window start, and as an error if the order is not en route by the
                                delivery time window end.)
                            </Body1>
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        css={css`
                            margin-top: 2rem;
                        `}
                    >
                        <PrimaryButton onClick={handleSave} disabled={loading}>
                            Save
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </TabCard>
        </>
    );
};

export default MilestoneThresholds;
